import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Braces, Database, Layers3 } from 'lucide-react'
import { filterQuizCases } from './catalog'
import type { QuizCaseFilters } from './catalog'
import type { QuizCaseDescriptor, QuizTestPageProps } from './types'

interface QuizCaseCatalogProps {
  cases: readonly QuizCaseDescriptor[]
  selectedCase: QuizCaseDescriptor
  onSelect: (quizCase: QuizCaseDescriptor) => void
  t?: QuizTestPageProps['t']
}

const STATUS_STYLE: Record<QuizCaseDescriptor['renderStatus'], string> = {
  renderable: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  'metadata-only': 'border-amber-300 bg-amber-50 text-amber-800',
  'missing-data': 'border-rose-300 bg-rose-50 text-rose-800',
}

type SelectFilterKey = Exclude<keyof QuizCaseFilters, 'declaredType'>

const FILTER_KEYS: readonly SelectFilterKey[] = [
  'family',
  'mode',
  'variant',
  'renderStatus',
  'answerState',
  'resultState',
  'tag',
]

const FILTER_FALLBACKS: Record<SelectFilterKey, string> = {
  family: 'Family',
  mode: 'Mode',
  variant: 'Variant',
  renderStatus: 'Status',
  answerState: 'Answer state',
  resultState: 'Result state',
  tag: 'Tag',
}

function label(t: QuizTestPageProps['t'], key: string, fallback: string): string {
  return t ? t(key) : fallback
}

export function QuizCaseCatalog({ cases, selectedCase, onSelect, t }: QuizCaseCatalogProps) {
  const types = useMemo(() => Array.from(new Set(cases.map((quizCase) => quizCase.declaredType))), [cases])
  const [selectedType, setSelectedType] = useState(selectedCase.declaredType)
  const [filters, setFilters] = useState<QuizCaseFilters>({})
  const typeCases = useMemo(
    () => filterQuizCases(cases, { ...filters, declaredType: selectedType }),
    [cases, filters, selectedType],
  )
  const filterOptions = useMemo<Record<SelectFilterKey, readonly string[]>>(() => ({
    family: Array.from(new Set(cases.map((quizCase) => quizCase.family))),
    mode: Array.from(new Set(cases.map((quizCase) => quizCase.mode))),
    variant: Array.from(new Set(cases.map((quizCase) => quizCase.variant))),
    renderStatus: Array.from(new Set(cases.map((quizCase) => quizCase.renderStatus))),
    answerState: Array.from(new Set(cases.map((quizCase) => quizCase.answerState))),
    resultState: Array.from(new Set(cases.map((quizCase) => quizCase.resultState))),
    tag: Array.from(new Set(cases.flatMap((quizCase) => quizCase.tags))).sort(),
  }), [cases])
  const renderableCount = useMemo(
    () => cases.filter((quizCase) => quizCase.renderStatus === 'renderable').length,
    [cases],
  )

  useEffect(() => {
    if (typeCases.length > 0 && !typeCases.some((quizCase) => quizCase.id === selectedCase.id)) {
      onSelect(typeCases[0])
    }
  }, [onSelect, selectedCase.id, typeCases])

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-800 bg-slate-950 text-slate-50 shadow-[0_24px_70px_-30px_rgba(15,23,42,0.8)]">
      <div className="relative overflow-hidden border-b border-slate-800 px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(148,163,184,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,.12)_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-300">
              <Braces className="h-4 w-4" />
              {label(t, 'quizCatalog.eyebrow', 'LC / renderer workbench')}
            </div>
            <h2 className="text-2xl font-black tracking-tight md:text-3xl">
              {label(t, 'quizCatalog.title', 'Quiz UI Case Catalog')}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">
              {label(t, 'quizCatalog.description', 'Inspect real API quizzes by type with explicit mode, variant, and result state.')}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 font-mono text-xs">
            <div className="rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2">
              <div className="text-slate-500">{label(t, 'quizCatalog.metrics.types', 'TYPES')}</div>
              <div className="mt-1 text-lg font-bold text-white">{types.length}</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2">
              <div className="text-slate-500">{label(t, 'quizCatalog.metrics.cases', 'CASES')}</div>
              <div className="mt-1 text-lg font-bold text-white">{cases.length}</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2">
              <div className="text-slate-500">{label(t, 'quizCatalog.metrics.live', 'LIVE')}</div>
              <div className="mt-1 text-lg font-bold text-emerald-300">{renderableCount}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-slate-800 bg-slate-900/70 px-4 py-3 md:px-6">
        <div className="flex gap-2 overflow-x-auto pb-1" aria-label={label(t, 'quizCatalog.typeFilter', 'Quiz type filter')}>
          {types.map((type) => {
            const active = selectedType === type
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setSelectedType(type)
                  const firstCase = filterQuizCases(cases, { ...filters, declaredType: type })[0]
                  if (firstCase) onSelect(firstCase)
                }}
                className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-xs font-semibold transition-colors ${
                  active
                    ? 'border-cyan-300 bg-cyan-300 text-slate-950'
                    : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500 hover:text-white'
                }`}
              >
                {type}
              </button>
            )
          })}
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {FILTER_KEYS.map((key) => {
            const filterLabel = label(t, `quizCatalog.filters.${key}`, FILTER_FALLBACKS[key])
            return (
              <label key={key} className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <span className="sr-only">{filterLabel}</span>
                <select
                  aria-label={filterLabel}
                  value={filters[key] ?? ''}
                  onChange={(event) => {
                    const value = event.target.value
                    setFilters((current) => ({ ...current, [key]: value || undefined }))
                  }}
                  className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 font-mono text-[length:max(11px,var(--quebon-form-font-size-min,0px))] normal-case tracking-normal text-slate-200"
                >
                  <option value="">{filterLabel}: {label(t, 'quizCatalog.filters.all', 'All')}</option>
                  {filterOptions[key].map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
            )
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(250px,0.8fr)_minmax(0,1.7fr)]">
        <div className="max-h-[440px] overflow-y-auto border-b border-slate-800 p-3 [content-visibility:auto] lg:border-b-0 lg:border-r">
          <div className="space-y-2">
            {typeCases.map((quizCase) => {
              const active = selectedCase.id === quizCase.id
              return (
                <button
                  key={quizCase.id}
                  type="button"
                  onClick={() => onSelect(quizCase)}
                  className={`w-full rounded-2xl border p-3 text-left transition-colors ${
                    active
                      ? 'border-cyan-400 bg-cyan-400/10'
                      : 'border-slate-800 bg-slate-900/50 hover:border-slate-600 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-bold text-white">
                      {label(t, quizCase.titleKey, quizCase.titleKey)}
                    </span>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[quizCase.renderStatus]}`}>
                      {quizCase.renderStatus}
                    </span>
                  </div>
                  <div className="mt-2 truncate font-mono text-[11px] text-slate-500">
                    {quizCase.apiQuizId ?? label(t, 'quizCatalog.noApiId', 'NO API ID')}
                  </div>
                </button>
              )
            })}
            {typeCases.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-slate-500">
                {label(t, 'quizCatalog.filters.empty', 'No cases match these filters.')}
              </p>
            ) : null}
          </div>
        </div>

        <div className="p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-cyan-300 px-2 py-1 font-mono text-xs font-black text-slate-950">{selectedCase.declaredType}</span>
            <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-xs text-slate-200">{selectedCase.mode}</span>
            <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-xs text-slate-200">{selectedCase.variant}</span>
            <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-xs text-slate-400">{selectedCase.answerState} / {selectedCase.resultState}</span>
          </div>
          <h3 className="mt-5 text-xl font-extrabold text-white">
            {label(t, selectedCase.titleKey, selectedCase.titleKey)}
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            {label(t, selectedCase.expectedKey, selectedCase.expectedKey)}
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <Database className="h-4 w-4" /> {label(t, 'quizCatalog.fields.apiSource', 'API source')}
              </div>
              <div className="mt-2 break-all font-mono text-sm text-white">{selectedCase.apiQuizId ?? label(t, 'quizCatalog.notAvailable', 'Not available')}</div>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <Layers3 className="h-4 w-4" /> {label(t, 'quizCatalog.fields.context', 'Context')}
              </div>
              <div className="mt-2 text-sm text-white">{label(t, selectedCase.contextKey, selectedCase.contextKey)}</div>
            </div>
          </div>
          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-800 bg-black/20 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                {label(t, 'quizCatalog.fields.resolvedProps', 'Resolved props')}
              </div>
              <pre className="mt-2 overflow-x-auto font-mono text-[11px] leading-5 text-cyan-100">
                {JSON.stringify({
                  mode: selectedCase.mode,
                  variant: selectedCase.variant,
                  answerState: selectedCase.answerState,
                  resultState: selectedCase.resultState,
                  ...selectedCase.rendererProps,
                }, null, 2)}
              </pre>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-black/20 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                {label(t, 'quizCatalog.fields.codeRefs', 'Code references')}
              </div>
              {selectedCase.codeRefs.length > 0 ? (
                <ul className="mt-2 space-y-1 font-mono text-[11px] leading-5 text-slate-300">
                  {selectedCase.codeRefs.map((codeRef) => <li key={codeRef} className="break-all">{codeRef}</li>)}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-slate-500">{label(t, 'quizCatalog.noCodeRefs', 'No code reference registered')}</p>
              )}
            </div>
          </div>
          {selectedCase.renderStatus !== 'renderable' ? (
            <div className="mt-4 flex gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <span>{label(t, `quizCatalog.statusHelp.${selectedCase.renderStatus}`, 'This case is documented without constructing a synthetic product screen.')}</span>
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedCase.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-slate-800 px-2.5 py-1 font-mono text-[10px] text-slate-300">#{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
